import { useEffect, useRef } from "react";
import { KEEP_DOCK_ALIVE_STORAGE_KEY, KEEP_DOCK_ALIVE_EXECUTE_CANCELLATION_TRESHOLD, KEEP_DOCK_ALIVE_LAST_REQUEST_TRESHOLD, DjiDockMode } from '../common/deviceConstants';
import { getDeviceInfo, getDeviceTelemetries, sendUpdateFlightAreasRequest } from "../common/deviceUtils";
import { sendActivateRemoteDebugging, sendDeactivateRemoteDebugging, sendTurnOnDrone } from "../common/remoteDebugUtils";
import { dockConnectionManager } from "src/helper/HubConnectionManager";
import { useBroadcastToFrontend } from "src/helper/useBroadcastToFrontend";
import useCurrentUserId from "src/helper/useCurrentUserId";
import { clientBroadcastMethod } from "src/services/common/constants";

function useDockKeepAliveOvserver() {
  const intervalId = useRef();
  const userId = useCurrentUserId();
  const broadcastToFrontend = useBroadcastToFrontend();

  const getDockKeepAliveConfig = () => {
    const keepAliveConfig = localStorage.getItem(KEEP_DOCK_ALIVE_STORAGE_KEY);
    const keepAliveConfigParsed = JSON.parse(keepAliveConfig);
    return keepAliveConfigParsed;
  }

  const saveDockKeepAliveConfig = (config) => {
    localStorage.setItem(KEEP_DOCK_ALIVE_STORAGE_KEY, JSON.stringify(config));
  }

  const updateDockLastMissionTimestamp = (dockId, timestamp) => {
    const keepAliveConfig = getDockKeepAliveConfig();
    if(!keepAliveConfig) return;

    const now = Date.now();
    const config = keepAliveConfig[dockId];

    if(config) {
      config.lastMissionTimestamp = timestamp || now;
      saveDockKeepAliveConfig(keepAliveConfig);
      console.log(`⌛️✅ Dock ${dockId} last mission timestamp updated.`);
    } else {
      console.log(`⌛️❌ Dock ${dockId} not found in keep alive config.`);
    }
  }

  const pauseDockKeepAlive = async (dockId) => {
    const keepAliveConfig = getDockKeepAliveConfig() || {};
    const config = keepAliveConfig[dockId];

    if (config) {
      try {
        config.paused = Date.now();

        broadcastToFrontend(clientBroadcastMethod.keepAlive.DOCK_DRONE_KEEP_ALIVE_PAUSE, { 
          deviceId: dockId,
          sourceUserId: userId,
        });

        saveDockKeepAliveConfig(keepAliveConfig);
        await sendDeactivateRemoteDebugging(dockId);

        console.log(`⌛️✅ Dock ${dockId} keep alive paused.`);
        return true;
      } catch (error) {
        console.error(`⌛️❌ Dock ${dockId} remote debugging deactivation failed.`, error);
        throw error;
      }
    }
    else {
      return false;
    }
  }

  const resumeDockKeepAlive = async (dockId) => {
    const keepAliveConfig = getDockKeepAliveConfig();
    const config = keepAliveConfig[dockId];

    if (config) {
      try {
        config.paused = false;
        saveDockKeepAliveConfig(keepAliveConfig);
        await sendActivateRemoteDebugging(dockId);
        await sendTurnOnDrone(dockId);
        console.log(`⌛️✅ Dock ${dockId} keep alive resumed.`);
      } catch (error) {
        console.error(`⌛️❌ Dock ${dockId} remote debugging activation failed.`, error);
        throw error;
      }
    }
  }

  const checkDockKeepAlive = async (deviceId) => {
    let keepAliveConfig = getDockKeepAliveConfig();
    if(!keepAliveConfig) return;

    const pormises = Object.keys(keepAliveConfig).map(async (dockId) => {
      const config = keepAliveConfig[dockId];
      const { duration, lastMissionTimestamp, timestamp } = config;

      if(deviceId && deviceId !== dockId) return;
      
      if(config?.paused) {
        console.log(`⌛️ℹ️ Dock ${dockId} keep alive skipped due to paused state.`);
        return;
      }

      const now = Date.now();
      const fromLastMission = now - lastMissionTimestamp;
      const fromLastRequest = now - timestamp;

      if(fromLastMission >= duration) {
        delete keepAliveConfig[dockId];
        saveDockKeepAliveConfig(keepAliveConfig);
        await sendDeactivateRemoteDebugging(dockId);

        console.log(`⌛️🏁 Dock ${dockId} keep alive cancelled after reaching requested idle duration.`);
        return;
      }

      if((getDeviceTelemetries(deviceId)?.mode_code !== DjiDockMode.IN_OPERATION && (deviceId || !timestamp || fromLastRequest >= KEEP_DOCK_ALIVE_LAST_REQUEST_TRESHOLD))) {
        try {
          const dockTelemetries = getDeviceTelemetries(dockId);
          const dockModeCode = dockTelemetries?.mode_code;
          const droneOnlineStatus = dockTelemetries?.sub_device?.device_online_status;

          if(dockModeCode !== DjiDockMode.REMOTE_DEBUGGING) {
            try {
              await sendActivateRemoteDebugging(dockId);
              console.log(`⌛️✅ Dock ${dockId} remote debugging activated for keep-alive.`);
            }
            catch (error) {
              console.error(`⌛️❌ Dock ${dockId} remote debugging activation failed for keep-alive.`, error);
            }
          }

          if(!droneOnlineStatus) {
            try {
              await sendTurnOnDrone(dockId);
              console.log(`⌛️✅ Dock ${dockId} drone turned on for keep-alive.`);
            }
            catch (error) {
              console.error(`⌛️❌ Dock ${dockId} drone turn on failed for keep-alive.`, error);
            }
          }

          keepAliveConfig[dockId].timestamp = now;
          saveDockKeepAliveConfig(keepAliveConfig);
          console.log(`⌛️✅ Dock ${dockId} keep alive cycle completed.`, now);
        } catch (error) {
          if(fromLastRequest >= KEEP_DOCK_ALIVE_EXECUTE_CANCELLATION_TRESHOLD) {
            delete keepAliveConfig[dockId];
            saveDockKeepAliveConfig(keepAliveConfig);
            console.log(`⌛️❌ Dock ${dockId} keep alive cancelled after reaching request failure threshold.`);
          }
        }
      }
    });

    await Promise.all(pormises);
  }

  const startProccessing = () => {
    intervalId.current = setInterval(() => {
      checkDockKeepAlive();
    }, 5000);

    dockConnectionManager.subscribeGroupMessages([
      {
        identity: 'cloudOsdMessages/*',
        name: ['osd'],
        handler: (message, meta) => {
          const data = message.data;
          const deviceId = meta?.fromUserId?.split('_')[0];

          if(data?.mode_code === DjiDockMode.IDLE) {
            const keepAliveConfig = getDockKeepAliveConfig();
            const config = keepAliveConfig?.[deviceId];

            if(config) {
              const deviceInfo = getDeviceInfo(deviceId);
              if(meta?.serialNumber === deviceInfo?.serialNumber && (!config?.paused || config?.paused < Date.now() - 5000)) {
                resumeDockKeepAlive(deviceId);
              }
            }
          }
        }
      }
    ]);
  }

  useEffect(() => {        
    return () => {
      clearInterval(intervalId.current);
    };
  }, []);

  return {
    getDockKeepAliveConfig,
    saveDockKeepAliveConfig,
    updateDockLastMissionTimestamp,
    pauseDockKeepAlive,
    resumeDockKeepAlive,
    checkDockKeepAlive,
    startProccessing,
  };
}

export default useDockKeepAliveOvserver;